import React, { useCallback } from 'react';
import {
  Col, Row, Stack, Spinner,
} from 'react-bootstrap';
import Snackbar from 'components/DesignSystem/Snackbar';
import SortingArrow from 'components/SortingArrow';
import { SortDirection } from 'constants/sorting';
import { FluxAboutDocument, AboutSortField } from 'types/Flux/About/fluxAboutDocument';
import AboutRow from 'components/Admin/Flux/About/AboutRow';

import 'components/Admin/AdminTable/table.scss';

interface AboutTableProps {
  documents: FluxAboutDocument[] | undefined;
  isFetching: boolean;
  onEdit: (doc: FluxAboutDocument) => void;
  onDelete: (doc: FluxAboutDocument) => void;
  sortBy: AboutSortField;
  sortDirection: SortDirection | undefined;
  setSortBy: (field: AboutSortField) => void;
  setSortDirection: (dir: SortDirection | undefined) => void;
}

function useFieldSorter(
  field: AboutSortField,
  setSortBy: (f: AboutSortField) => void,
  setSortDirection: (d: SortDirection | undefined) => void,
) {
  return useCallback((direction?: SortDirection) => {
    setSortBy(field);
    setSortDirection(direction);
  }, [field, setSortBy, setSortDirection]);
}

const COLUMNS: { title: string; field: AboutSortField; md: number }[] = [
  { title: 'ID', field: 'id', md: 1 },
  { title: 'TÍTULO', field: 'titulo', md: 3 },
  { title: 'DESCRIÇÃO', field: 'descricao', md: 3 },
  { title: 'AUTOR', field: 'autor', md: 2 },
  { title: 'ORDEM', field: 'ordem', md: 1 },
];

export default function AboutTable({
  documents,
  isFetching,
  onEdit,
  onDelete,
  sortBy,
  sortDirection,
  setSortBy,
  setSortDirection,
}: AboutTableProps) {
  const sorterID = useFieldSorter('id', setSortBy, setSortDirection);
  const sorterTitulo = useFieldSorter('titulo', setSortBy, setSortDirection);
  const sorterDescricao = useFieldSorter('descricao', setSortBy, setSortDirection);
  const sorterAutor = useFieldSorter('autor', setSortBy, setSortDirection);
  const sorterOrdem = useFieldSorter('ordem', setSortBy, setSortDirection);

  const sorters: Record<AboutSortField, (d?: SortDirection) => void> = {
    id: sorterID,
    titulo: sorterTitulo,
    descricao: sorterDescricao,
    autor: sorterAutor,
    ordem: sorterOrdem,
  };

  return (
    <div className="admin-table-main">
      <Row className="fw-bold admin-table-header">
        {COLUMNS.map((col) => (
          <Col key={col.field} md={col.md}>
            <Stack direction="horizontal">
              {col.title}
              <SortingArrow
                field={col.field}
                sortBy={sortBy}
                sortDirection={sortDirection}
                sort={sorters[col.field]}
              />
            </Stack>
          </Col>
        ))}
        <Col md="2">AÇÕES</Col>
      </Row>
      {isFetching && (
        <Row>
          <Spinner className="mx-auto mt-3" animation="border" />
        </Row>
      )}
      {!isFetching && documents && documents.length > 0
        && documents.map((doc) => (
          <AboutRow
            key={doc.id}
            document={doc}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      {!isFetching && (!documents || documents.length === 0) && (
        <Snackbar>
          Nenhum documento encontrado
        </Snackbar>
      )}
    </div>
  );
}
